import { useCallback, useEffect, useMemo, useState } from "react"
import { DateTime } from "luxon"
import Button from "../components/Button"
import Icon from "../components/Icon"
import { COLOR, MONTHS, WEEKDAYS } from "../data/variables"
import { ICON } from "../components/Icon/data"

type UseCalendar = {
  date: string
  onChange: (key: string) => string
}

export const useCalendar = ({ date, onChange }: UseCalendar) => {
  const [currMonth, setCurrMonth] = useState<number>(DateTime.fromISO(date).month)
  const [currYear, setCurrYear] = useState<number>(DateTime.fromISO(date).year)

  useEffect(() => {
    const luxonDate = DateTime.fromISO(date)
    const month = luxonDate.month
    const year = luxonDate.year
    setCurrMonth(month)
    setCurrYear(year)
  }, [date])

  const changeYear = useCallback((offset = 0) => {
    setCurrYear(currYear + offset)
  }, [currYear])

  const changeMonth = useCallback((offset = 0) => {
    const newMonth = currMonth + offset
    if (newMonth > 12) {
      setCurrMonth(1)
      changeYear(1)
    } else if (newMonth < 1) {
      setCurrMonth(12)
      changeYear(-1)
    } else {
      setCurrMonth(newMonth)
    }
  }, [currYear, currMonth, changeYear])

  const calendarHeader = useMemo(() => {
    const header = `${MONTHS[currMonth]} ${currYear}`
    return (
      <div className="Datepicker__month-header">
        <Button
          type="ghost"
          size="s"
          label={<Icon icon={ICON.CHEVRON_LEFT} color={COLOR.GRAY_60} />}
          onClick={() => changeMonth(-1)}
        />
        <div className="Datepicker__month-header__label">{header}</div>
        <Button
          type="ghost"
          size="s"
          label={<Icon icon={ICON.CHEVRON_RIGHT} color={COLOR.GRAY_60} />}
          onClick={() => changeMonth(1)}
        />
      </div>
    )
  }, [currMonth, currYear])

  const selectDate = useCallback((day: number) => {
    const newDate = DateTime.fromObject({ month: currMonth, day, year: currYear })
    onChange(newDate.toISODate())
  }, [currMonth, currYear, onChange])

  const calendarDates = useMemo(() => {
    const luxonDate = DateTime.fromObject({ month: currMonth, year: currYear })
    const daysInMonth = luxonDate.daysInMonth
    const startWeekday = luxonDate.startOf('month').weekday
    const todayMonth = DateTime.now().month
    const todayYear = DateTime.now().year
    const nowInMonth = todayMonth === luxonDate.month && todayYear === luxonDate.year
    const monthArr = []
    if (startWeekday !== 1) {
      const weekArr = []
      const lastMonthDays = luxonDate.minus({ month: 1 }).daysInMonth
      let day = lastMonthDays - startWeekday + 2
      let disabled = true
      let isToday = false
      Object.keys(WEEKDAYS).forEach(() => {
        if (day > lastMonthDays) {
          day = 1
          disabled = false
        }

        if (nowInMonth && day === parseInt(DateTime.now().toFormat('D')) && !disabled) {
          isToday = true
        }

        const temp = {
          day,
          disabled,
          isToday
        }
        weekArr.push(temp)
        day = day + 1
        isToday = false
      })
      monthArr.push(weekArr)
    }

    const firstDay = monthArr.length ? monthArr[0][monthArr[0].length - 1].day + 1 : 1
    let numWeeks = Math.ceil((daysInMonth - firstDay + 1) / 7)
    let weekArr = []
    let day = firstDay
    let disabled = false
    let isToday = false
    while (numWeeks !== 0) {
      Object.keys(WEEKDAYS).forEach(() => {
        if (day === daysInMonth + 1) {
          day = 1
          disabled = true
        }

        if (nowInMonth && day === parseInt(DateTime.now().toFormat('D')) && !disabled) {
          isToday = true
        }

        const temp = {
          day,
          disabled,
          isToday
        }
        weekArr.push(temp)
        day = day + 1
        isToday = false
      })
      monthArr.push(weekArr)
      numWeeks = numWeeks - 1
      weekArr = []
    }

    const isSelectedMonth = DateTime.fromISO(date).month === currMonth && DateTime.fromISO(date).year === currYear
    const selectedDay = parseInt(DateTime.fromISO(date).toFormat('D'))
    return (
      <>
        {monthArr.map((week) => {
          return (
            <div className="Datepicker__month-wrapper__week">
              {week.map((d) => {
                const { disabled, day, isToday } = d
                const classArr = ['Datepicker__month-date']
                if (disabled) classArr.push('disabled')
                if (isToday) classArr.push('today')
                if (isSelectedMonth && day === selectedDay && !disabled) classArr.push('selected')
                return (
                  <div
                    className={classArr.join(' ')}
                    role="button"
                    tabIndex={0}
                    onClick={() => disabled ? {} : selectDate(day)}
                  >
                    {day}
                  </div>
                )
              })}
            </div>
          )
        })}
      </>
    )
  }, [date, currMonth, currYear])

  const getMonthCalendar = useMemo(() => {
    const luxonDate = DateTime.fromISO(date)
    if (luxonDate.isValid) {
      return (
        <div className="Datepicker__month">
          {calendarHeader}
          <div className="Datepicker__month-wrapper">
            <div className="Datepicker__month-wrapper__week">
              {Object.keys(WEEKDAYS).map((key) => {
                const weekday = WEEKDAYS[key]
                // only return the first letter of the weekday
                return (
                  <div className="Datepicker__month-date">
                    {weekday[0]}
                  </div>
                )
              })}
            </div>
            {calendarDates}
          </div>
        </div>
      )
    }

    return null
  }, [currMonth, currYear, date, calendarDates])

  return {
    getMonthCalendar
  }
}
